import axios from "axios";
import {useParams} from "react-router-dom";
import {useCallback, useEffect, useState} from "react";
import {GetDataApi} from "../components/types/GetDataApi";
import {emptyFxTransaction, FxTransaction} from "../domain/transaction/FxTransaction";
import {emptyPageDto, PageDto} from "../domain/PageDto";
import {RefreshDataFunction} from "../components/types/RefreshDataFunction";
import {SearchDataFunction} from "../components/types/SearchDataFunction";
import {defaultTransactionSearchTerm, TransactionSearchTerm} from "../domain/transaction/TransactionSearchTerm";
import {getGatewayUrl} from "../components/EnvironmentConfiguration";
import {convertTransactionSearchTerm} from "./TransactionSearchTermDto";

export function useFindFxTransactions(): RefreshDataFunction<PageDto<FxTransaction>> {
    const [transactions, setTransactions] = useState<PageDto<FxTransaction>>(emptyPageDto());
    const { transactionId, assetManager } = useParams();
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const apiFunction = useCallback(async () => {
        setLoading(true);
        try {
            if (transactionId && assetManager) {
                const body = JSON.stringify({
                    "externalTransactionId": transactionId,
                    "assetManager": assetManager
                });
                const config = {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                };
                const response = await axios.post(getGatewayUrl() + '/transaction/v1/fx/findByExternalId', body, config);
                setTransactions(response.data as PageDto<FxTransaction>);
            }
            else {
                const response = await axios.get(getGatewayUrl() + '/transaction/v1/fx');
                setTransactions(response.data as PageDto<FxTransaction>);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setLoading(false);
        }
    }, [ transactionId, assetManager ]);

    useEffect(() => {
        void apiFunction();
    }, [ apiFunction, transactionId, assetManager ]);

    return { data: transactions, loading, error, apiFunction }
}

export function useFindByFxTransactionId(): GetDataApi<FxTransaction> {
    const { transactionId } = useParams();
    const [transaction, setTransaction] = useState<FxTransaction>(emptyFxTransaction);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get(getGatewayUrl() + "/transaction/v1/fx/" + transactionId);
                setTransaction(response.data as FxTransaction);
            } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
            } finally {
                setLoading(false);
            }
        };
        void fetchData();
    }, [ transactionId ]);

    return { data: transaction, loading, error }
}

export function useSearchFxTransactions(initialSearchTerm: TransactionSearchTerm | null): SearchDataFunction<TransactionSearchTerm, PageDto<FxTransaction>> {
    const defaultSearchTerm = initialSearchTerm ?? defaultTransactionSearchTerm();
    const [transactions, setTransactions] = useState<PageDto<FxTransaction>>(emptyPageDto());
    const [searchTerm, setSearchTerm] = useState<TransactionSearchTerm>(defaultSearchTerm);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const apiFunction = useCallback(
        async (searchTerm: TransactionSearchTerm) => {
            setLoading(true);
            const dto = convertTransactionSearchTerm(searchTerm);
            const config = {
                headers: {
                    'Content-Type': 'application/json'
                }
            };
            try {
                const response = await axios.post(getGatewayUrl() + '/transaction/v1/fx/search', dto, config);
                setTransactions(response.data as PageDto<FxTransaction>);
            } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
            } finally {
                setLoading(false);
            }
        },
        [ searchTerm ]
    );

    useEffect(() => { void apiFunction(searchTerm); },
        [ apiFunction, searchTerm ]
    );

    return { data: transactions, searchTerm, loading, error, apiFunction, setSearchTermFunction: setSearchTerm }
}